/**
 * The availability badge a product carries wherever it is shown (spec 13 R4c).
 *
 * Lifted out of the products list when the detail page needed the same thing
 * (AGENTS §5 rule 15). It exists so the *decision* — which state, and whether to
 * draw anything at all — is made once: two screens each deciding for themselves
 * is how they end up disagreeing about the same product.
 */
import type { ReactNode } from "react";
import { StatusPill } from "@flint/ui";
import { listingStockState, type ListingStockState, type StockEntry } from "./size-ratio.ts";

/**
 * `in-stock` and `untracked` draw nothing. A badge on every row is noise — the
 * point is that a quiet Status cell means the product needs nothing, and
 * grandfathered entries (written before quantities were recorded) shouldn't nag
 * about data nobody was asked for.
 *
 * "Below minimum" is amber, not red: the goods exist, which is a different
 * problem from having none. It is what the storefront renders UNAVAILABLE.
 */
const PILL: Record<ListingStockState, ReactNode> = {
  untracked: null,
  "in-stock": null,
  "out-of-stock": (
    <StatusPill tone="bad" size="sm">
      Out of stock
    </StatusPill>
  ),
  "below-minimum": (
    <StatusPill tone="warn" size="sm">
      Below minimum
    </StatusPill>
  ),
};

export function StockBadge({
  entries,
  visibility,
}: {
  entries: readonly StockEntry[];
  visibility: "draft" | "published" | "archived";
}) {
  // Archived draws nothing, for the same reason the availability tabs skip
  // archived products (R4d): one that cannot be sold has no stock worth acting
  // on — and badging a product the tabs won't list is exactly the disagreement
  // the shared derivation exists to prevent.
  if (visibility === "archived") return null;
  return <>{PILL[listingStockState(entries)]}</>;
}
